import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import {
  isResolvedChallenge,
  isValidJankenChallengeId,
  isJankenChallengeExpired,
  canGrantJankenReward,
  getJankenPendingResultById,
} from './janken.getters';
import {
  markChallengeResolved,
  resetJankenDaily,
  recordJankenReward,
  deleteJankenPendingResult,
  resolveReceiverOutcome,
  setJankenTutorialSeen,
} from './janken.modifiers';
import {
  deriveJankenOutcome,
  rollJankenReward,
  jankenMapKey,
} from './janken.utils';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { Feature } from '../analytics';

export const jankenActions = createActions({
  /**
   * Generate a unique challenge ID for sharing.
   * Format: `jnk_{timestamp}_{random}`.
   * Nothing is saved to state — the ID is returned to the client for sharing.
   * Timestamp is extractable via `parseJankenChallengeTimestamp()` for client-side expiry checks.
   *
   * @example
   * const challengeId = await StateObserver.invoke.generateJankenChallengeId();
   * // => 'jnk_1708300000000_482917365'
   */
  generateJankenChallengeId: action((_state, _args: void, api) => {
    const timestamp = api.date.now();
    const randomInt = Math.floor(api.math.random() * 36 ** 8);
    const random = randomInt.toString(36).padStart(8, '0');
    return `jnk_${timestamp}_${random}`;
  }),

  /**
   * Resolve a janken challenge (called by the receiver when they tap the CTA).
   * Runs on the **receiver's** state.
   *
   * - Replay guard via `isResolvedChallenge` prevents duplicate processing
   * - Derives deterministic outcome from challengeId + senderId + receiverId
   * - Grants reward if receiver won and within daily limits
   * - Notifies sender via postMessage (creates a pending result on sender's state)
   *
   * @example
   * await StateObserver.invoke.resolveJankenChallenge({
   *   challengeId: 'jnk_1708300000000_482917365',
   *   senderId: 'Uf4a3b2c1d0e9f8a7b6c5d4e3f2a1b0cd',
   * });
   */
  resolveJankenChallenge: action(
    (state, args: { challengeId: string; senderId: string }, api) => {
      const { challengeId, senderId } = args;

      // Validate challengeId format
      if (!isValidJankenChallengeId(challengeId)) {
        return;
      }

      const now = api.date.now();
      const receiverId = state.id;

      // Reject self-play
      if (senderId === receiverId) {
        return;
      }

      // Reject expired challenges (> 24h)
      if (isJankenChallengeExpired(challengeId, now)) {
        return;
      }

      // Replay guard — already resolved this challenge from this sender
      if (isResolvedChallenge(state, challengeId, senderId)) {
        return;
      }

      // Mark challenge as resolved for replay guard. Must precede reward granting to prevent duplicates.
      markChallengeResolved(state, challengeId, senderId, now);

      // Clean up any lingering receiver-pending result (migration from pre-stateless flow).
      const receiverPendingKey = jankenMapKey(challengeId, senderId);
      if (state.janken?.pendingResults?.[receiverPendingKey]) {
        deleteJankenPendingResult(state, receiverPendingKey);
      }

      // Derive outcome, grant reward if won, notify sender via postMessage + OA
      resolveReceiverOutcome(
        state,
        challengeId,
        senderId,
        receiverId,
        now,
        api,
      );
    },
  ),

  /**
   * Claim a pending sender janken result.
   * Runs on the **sender's** state.
   *
   * - Looks up the sender pending result by key
   * - Derives deterministic outcome
   * - Grants reward if sender won and within daily limits
   * - Deletes the pending result entry
   *
   * Receiver pending results are resolved via `resolveJankenChallenge` and
   * should never be passed to this action.
   *
   * @example
   * await StateObserver.invoke.claimJankenResult({
   *   pendingResultId: 'jnk_1708300000000_482917365_U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b',
   * });
   */
  claimJankenResult: action((state, args: { pendingResultId: string }, api) => {
    const { pendingResultId } = args;

    const pending = getJankenPendingResultById(state, pendingResultId);
    if (!pending) {
      return;
    }

    const now = api.date.now();

    if (isJankenChallengeExpired(pending.challengeId, now)) {
      deleteJankenPendingResult(state, pendingResultId);
      return;
    }

    const senderId = state.id;

    if (senderId === pending.receiverId) {
      deleteJankenPendingResult(state, pendingResultId);
      return;
    }

    resetJankenDaily(state, now);

    const { result } = deriveJankenOutcome(
      pending.challengeId,
      senderId,
      pending.receiverId,
    );

    if (
      result.sender === 'win' &&
      canGrantJankenReward(state, pending.receiverId, now)
    ) {
      const reward = rollJankenReward(
        pending.challengeId,
        senderId,
        pending.receiverId,
      );
      grantMatch2Rewards(
        state,
        [reward],
        { feature: Feature.JANKEN._, $subFeature: Feature.JANKEN.LUCKY_ITEM },
        api,
      );
      recordJankenReward(state, pending.receiverId, now);
    }

    deleteJankenPendingResult(state, pendingResultId);
  }),

  /**
   * Mark the janken tutorial as seen. Called after the info popup
   * auto-shows on the user's first janken open.
   */
  setJankenTutorialSeen: action((state, _args: void, api) => {
    setJankenTutorialSeen(state, api.date.now());
  }),
});
